import type { GameProps } from '../../types';

import * as React from 'react';
import styled from '@emotion/styled';
import { useEditorHandlers } from './@editor';
import { useTheme } from '@mui/material/styles';
import { useAppContext } from '../../contexts/AppContext';
import { useHighlightHandlers } from '../../contexts/HighlightContext/Highlight';
import useLocalStorage from '../../utils/use-local-storage'
import { Typography as MuiTypography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Popper from '@mui/material/Popper';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Badge from '@mui/material/Badge';
import { Link } from '../Link';
import { CardAsset } from '../Card';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

interface LastPlayedMenuProps {
  size?: 'small' | 'medium' | 'large';
  edge?: false | 'end' | 'start';
  variant?: 'dot' | 'standard';
  overlap?: 'circular' | 'rectangular';
}


const Typography = React.forwardRef<HTMLInputElement, any>((props, ref) =>
  useHighlightHandlers(MuiTypography, { forwardedRef: ref, ...props })
);

const IconButtonStyled = styled(IconButton)(({ theme }) => ({
  '&.Mui-disabled svg': {
    color: theme.theme_component_last_played_icon_disabled_color || theme.palette.text.disabled,
  },
}));

const ItemLabel = styled(Typography)(({ theme }) => ({
  color: theme.theme_component_last_played_label_color || theme.palette.text.primary,
}));

const PaperStyled = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.theme_component_last_played_paper_background_color || theme.palette.background.paper,
})) as typeof Paper;

const LastPlayedIcon = styled(AccessTimeIcon)(({ theme }) => ({
  color: theme.theme_component_last_played_icon_color || theme.palette.primary.contrastText,
}))

const ListItemIconStyled = styled(ListItemIcon)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginRight: theme.spacing(2),
  width: theme.spacing(4),
  aspectRatio: '1/1',
})) as typeof ListItemIcon;

export const LastPlayedMenu = (props: LastPlayedMenuProps) => {
  const { size, edge, variant, overlap } = props;
  const theme = useTheme();
  const appContext = useAppContext();
  const { assetUrl } = appContext.useConfig();

  const [gamesCurrent] = useLocalStorage<GameProps[] | undefined>('lastplayed:current', []);
  const [gamesPrevious, setGamesPrevious] = useLocalStorage<GameProps[] | undefined>('lastplayed:previous', []);

  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [open, setOpen] = React.useState(false);
  const prevOpen = React.useRef<boolean>(open);

  const labelRefs = React.useRef<React.Ref<HTMLAnchorElement> | []>([]);

  // Todo: Fix labelRefs.current typescript problem.
  // @ts-ignore
  labelRefs.current = gamesCurrent.map((game: GameProps, index: number) => {
    // @ts-ignore
    return labelRefs.current[index] ?? React.createRef();
  });

  const getDiff = (previousGames: GameProps[] | undefined, currentGames: GameProps[] | undefined) => {
    let badgeCount = 0;
    if (!previousGames || !currentGames) return badgeCount;
    previousGames.forEach((previous: GameProps) => {
      currentGames.forEach((recent: GameProps) => {
        if (recent.id === previous.id) badgeCount++;
      });
    });
    return currentGames.length - badgeCount;
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
    if (!open) setGamesPrevious(gamesCurrent);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    // @ts-ignore
    if (event && anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  React.useEffect(() => {
    // Return focus to the button when we transitioned from !open -> open.
    if (anchorRef.current && prevOpen.current && !open) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  React.useEffect(() => {
    setGamesPrevious(gamesPrevious);
  }, []);

  return (
    <>
      <IconButtonStyled
        ref={anchorRef}
        id="last-played-select-button"
        aria-controls={open ? 'last-played-select-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        size={size}
        edge={edge}
        disabled={gamesCurrent && gamesCurrent.length <= 0}
        onClick={handleToggle}
        // onMouseEnter={handleToggle}
        {...useEditorHandlers('Last Played Menu')}
      >
        <Badge
          badgeContent={getDiff(gamesPrevious, gamesCurrent)}
          max={10}
          color="secondary"
          variant={variant}
          overlap={overlap}
        >
          <LastPlayedIcon/>
        </Badge>
      </IconButtonStyled>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-end"
        transition
        disablePortal
        style={{
          zIndex: 1000,
          maxWidth: `calc(100vw - ${theme.spacing(1)}px)`,
        }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'right top' : 'right bottom',
            }}
          >
            <PaperStyled>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="last-played-select-menu"
                  aria-labelledby="last-played-select-button"
                  onKeyDown={handleListKeyDown}
                >
                  {gamesCurrent?.map((game: GameProps, index: number) => (
                    game.id && (
                      <MenuItem
                        key={game.id}
                        component={Link}
                        href={`/game/${game.slug}`}
                        title={game.name || undefined}
                      >
                        <ListItemIconStyled>
                          <CardAsset
                            src={`${assetUrl}${game.images.cover1x1 || game.images.cover}/`}
                            alt={game.name || 'Cover'}
                            width={30}
                            height={30}
                            layout="responsive"
                            sizes="30px"
                          />
                        </ListItemIconStyled>
                        <ItemLabel
                          // @ts-ignore
                          ref={labelRefs.current[index]}
                          variant="body1"
                          textAlign="left"
                          noWrap
                        >
                          {game.name}
                        </ItemLabel>
                      </MenuItem>
                    )
                  ))}
                </MenuList>
              </ClickAwayListener>
            </PaperStyled>
          </Grow>
        )}
      </Popper>
    </>
  );
}